import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { query, where, getDocs, onSnapshot, orderBy, limit } from "firebase/firestore";
import { NotificationsNone } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import ErrorIcon from "@mui/icons-material/Error";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import CampaignIcon from "@mui/icons-material/Campaign";
import CollectionsIcon from "@mui/icons-material/Collections";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/system";
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { cloudflareCDN } from "ui";

import { notificationsDB, notificationsViewedDB, updateRecord } from "../libs/firebase";
import { useLoginState } from "../studioxr/auth/LoginState.js";
import { generateID } from "../libs/util.js";
import { formatRelativeShort } from "../libs/relativeTime";
import {
  NotificationsInterface,
  NotificationsViewedInterface,
  ProfileInterface,
} from "ts-interfaces";
import { BarIconButton } from "../buttons/ButtonStyles";

const FETCH_LIMIT = 20;

function notifIcon(type?: string) {
  if (type === "video") return <VideocamIcon fontSize="small" />;
  if (type === "closeup") return <CollectionsIcon fontSize="small" />;
  if (type === "error") return <ErrorIcon fontSize="small" sx={{ color: "#EB0014" }} />;
  if (type === "image") return <PhotoCameraIcon fontSize="small" />;
  return <AddCircleIcon fontSize="small" />;
}

type ParsedLink =
  | { kind: "internal"; href: string }
  | { kind: "external"; href: string }
  | { kind: "invalid" };

export function parseNotificationLink(link?: string): ParsedLink {
  if (!link) return { kind: "invalid" };
  if (link.startsWith("/") && !link.startsWith("//")) {
    return { kind: "internal", href: link };
  }
  try {
    const url = new URL(link);
    if (url.protocol === "http:" || url.protocol === "https:") {
      return { kind: "external", href: url.href };
    }
  } catch {
    // not a valid absolute URL
  }
  return { kind: "invalid" };
}

const StyledBadge = styled(Badge)(() => ({
  "& .MuiBadge-badge": {
    right: 2,
    top: 2,
    height: "16px",
    minWidth: "16px",
    padding: "0 4px",
    borderRadius: "8px",
    fontSize: "10px",
    backgroundColor: "#EB0014",
  },
}));

const ACTIVITY_LIST_MAX_HEIGHT = 400;

const StyledMenu = styled(Menu)(() => ({
  "& .MuiPaper-root": {
    width: "380px",
    maxWidth: "calc(100vw - 24px)",
    maxHeight: "520px",
    marginTop: "8px",
    overflow: "hidden",
    borderRadius: "16px",
    border: "1px solid rgba(0, 0, 0, 0.06)",
    boxShadow: "0 24px 48px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.08)",
  },
  "& .MuiMenu-list": {
    padding: 0,
    backgroundColor: "#ffffff",
  },
}));

function isUnread(
  notification: NotificationsInterface,
  notificationsViewed: NotificationsViewedInterface[],
) {
  return !notificationsViewed.some((v) => v.notification_id === notification.id);
}

export default function Notifications() {
  const profile: ProfileInterface | any = useLoginState((state) => state.profile);
  const [activityItems, setActivityItems] = useState<NotificationsInterface[]>([]);
  const [announcementItems, setAnnouncementItems] = useState<NotificationsInterface[]>([]);
  const [notificationsViewed, setNotificationsViewed] = useState<NotificationsViewedInterface[]>(
    [],
  );

  useEffect(() => {
    if (profile == null) {
      setActivityItems([]);
      setAnnouncementItems([]);
      setNotificationsViewed([]);
      return () => {};
    }
    const uid = profile.uid;

    const activityQuery = query(
      notificationsDB as any,
      where("owner_id", "==", uid),
      orderBy("updatedAt", "desc"),
      limit(FETCH_LIMIT),
    );
    const unsubActivity = onSnapshot(
      activityQuery,
      (snapshot) => {
        const docs = snapshot.docs.map(
          (doc) =>
            ({ id: doc.id, ...(doc.data() as Omit<NotificationsInterface, "id">) }) as NotificationsInterface,
        );
        setActivityItems(docs.filter((n) => n.dismissed !== true));
      },
      (error) => console.error("Error listening to activity notifications:", error),
    );

    const announcementsQuery = query(
      notificationsDB as any,
      where("owner_id", "==", "all"),
      orderBy("updatedAt", "desc"),
      limit(FETCH_LIMIT),
    );
    const unsubAnnouncements = onSnapshot(
      announcementsQuery,
      (snapshot) => {
        setAnnouncementItems(
          snapshot.docs.map(
            (doc) =>
              ({ id: doc.id, ...(doc.data() as Omit<NotificationsInterface, "id">) }) as NotificationsInterface,
          ),
        );
      },
      (error) => console.error("Error listening to announcement notifications:", error),
    );

    const viewedQuery = query(
      notificationsViewedDB as any,
      where("user_id", "==", uid),
      orderBy("updatedAt", "desc"),
    );
    getDocs(viewedQuery)
      .then((viewedDoc) => {
        if (profile?.uid !== uid) return;
        setNotificationsViewed(
          viewedDoc.docs.map((doc) => doc.data() as NotificationsViewedInterface),
        );
      })
      .catch((error) => console.error("Error fetching notifications viewed: ", error));

    return () => {
      unsubActivity();
      unsubAnnouncements();
    };
  }, [profile?.uid]);

  const markRead = (notification: NotificationsInterface) => {
    if (!profile?.uid) return;
    if (!isUnread(notification, notificationsViewed)) return;
    const data = {
      id: generateID(),
      notification_id: notification.id,
      user_id: profile.uid,
      updatedAt: Date.now(),
    };
    setNotificationsViewed((prev) => [...prev, data]);
    updateRecord(data.id, "notifications_viewed", data).catch((error) => {
      console.error("Error updating notifications viewed: ", error);
    });
  };

  const dismissActivity = (notification: NotificationsInterface) => {
    if (notification.owner_id === "all") return;
    setActivityItems((prev) => prev.filter((n) => n.id !== notification.id));
    updateRecord(notification.id, "notifications", { dismissed: true }).catch((error) =>
      console.error("Error dismissing notification:", error),
    );
  };

  const markAllRead = (items: NotificationsInterface[]) => {
    if (!profile?.uid) return;
    const unread = items.filter((n) => isUnread(n, notificationsViewed));
    if (unread.length === 0) return;
    const newViews = unread.map((notification) => ({
      id: generateID(),
      notification_id: notification.id,
      user_id: profile.uid,
      updatedAt: Date.now(),
    }));
    setNotificationsViewed((prev) => [...prev, ...newViews]);
    newViews.forEach((data) => {
      updateRecord(data.id, "notifications_viewed", data).catch((error) =>
        console.error("Error updating notifications viewed:", error),
      );
    });
  };

  return (
    <ShowNotifications
      activityItems={activityItems}
      announcementItems={announcementItems}
      notificationsViewed={notificationsViewed}
      onItemClick={markRead}
      onDismiss={dismissActivity}
      onMarkAllRead={markAllRead}
    />
  );
}

interface ShowNotificationsProps {
  activityItems: NotificationsInterface[];
  announcementItems: NotificationsInterface[];
  notificationsViewed: NotificationsViewedInterface[];
  onItemClick: (notification: NotificationsInterface) => void;
  onDismiss: (notification: NotificationsInterface) => void;
  onMarkAllRead: (items: NotificationsInterface[]) => void;
}

export function ShowNotifications({
  activityItems,
  announcementItems,
  notificationsViewed,
  onItemClick,
  onDismiss,
  onMarkAllRead,
}: ShowNotificationsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [tab, setTab] = useState<"activity" | "announcements">("activity");
  const open = Boolean(anchorEl);

  const activityUnreadCount = useMemo(
    () => activityItems.filter((n) => isUnread(n, notificationsViewed)).length,
    [activityItems, notificationsViewed],
  );
  const announcementUnreadCount = useMemo(
    () => announcementItems.filter((n) => isUnread(n, notificationsViewed)).length,
    [announcementItems, notificationsViewed],
  );
  const totalUnread = activityUnreadCount + announcementUnreadCount;
  const activeItems = tab === "activity" ? activityItems : announcementItems;

  const handleClose = () => setAnchorEl(null);
  const handleIconClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    if (activityUnreadCount === 0 && announcementUnreadCount > 0) {
      setTab("announcements");
    } else {
      setTab("activity");
    }
  };

  return (
    <>
      <BarIconButton
        sx={{ display: { xs: "none", sm: "inline" }, padding: "8px" }}
        id="notificationsIconBtn"
        onClick={handleIconClick}
      >
        <StyledBadge
          id="notificationsBadgeCount"
          badgeContent={totalUnread}
          color="primary"
          style={{ cursor: "pointer" }}
        >
          <NotificationsNone style={{ color: "black", fontSize: "28px" }} />
        </StyledBadge>
      </BarIconButton>

      {open && (
        <StyledMenu
          id="notificationsMenu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Box sx={{ padding: "12px 12px 8px" }}>
            <Box
              role="tablist"
              sx={{
                display: "flex",
                padding: "4px",
                backgroundColor: "#f1f1f3",
                borderRadius: "10px",
              }}
            >
              <SegmentedTab
                id="notificationsTabActivity"
                label="Activity"
                unreadCount={activityUnreadCount}
                active={tab === "activity"}
                onClick={() => setTab("activity")}
              />
              <SegmentedTab
                id="notificationsTabAnnouncements"
                label="Notifications"
                unreadCount={announcementUnreadCount}
                active={tab === "announcements"}
                onClick={() => setTab("announcements")}
              />
            </Box>
          </Box>

          <Box
            sx={{
              maxHeight: `${ACTIVITY_LIST_MAX_HEIGHT}px`,
              overflowY: "auto",
              backgroundColor: "#ffffff",
            }}
          >
            {tab === "activity" ? (
              <ActivityList
                items={activityItems}
                notificationsViewed={notificationsViewed}
                onItemClick={(n) => {
                  onItemClick(n);
                  handleClose();
                }}
                onDismiss={onDismiss}
              />
            ) : (
              <AnnouncementList
                items={announcementItems}
                notificationsViewed={notificationsViewed}
                onItemClick={(n) => {
                  onItemClick(n);
                  handleClose();
                }}
              />
            )}
          </Box>

          {activeItems.length > 0 && (
            <Box
              sx={{
                borderTop: "1px solid #ececec",
                textAlign: "center",
                padding: "5px",
                backgroundColor: "#f7f7f8",
              }}
            >
              <Button
                id="notificationsMarkAllReadBtn"
                onClick={() => onMarkAllRead(activeItems)}
                sx={{
                  textTransform: "none",
                  fontSize: "13px",
                  color: "#888",
                  padding: "5px 12px",
                  minHeight: 0,
                  lineHeight: 1.4,
                }}
              >
                Mark all as read
              </Button>
            </Box>
          )}
        </StyledMenu>
      )}
    </>
  );
}

function SegmentedTab({
  id,
  label,
  unreadCount,
  active,
  onClick,
}: {
  id: string;
  label: string;
  unreadCount: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <ButtonBase
      id={id}
      role="tab"
      aria-selected={active}
      onClick={onClick}
      sx={{
        flex: 1,
        padding: "8px 12px",
        borderRadius: "8px",
        backgroundColor: active ? "#ffffff" : "transparent",
        boxShadow: active ? "0 1px 2px rgba(0, 0, 0, 0.08)" : "none",
        transition: "background-color 0.15s ease, box-shadow 0.15s ease",
        "&:hover": { backgroundColor: active ? "#ffffff" : "rgba(255, 255, 255, 0.5)" },
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
        <Typography
          component="span"
          sx={{
            fontSize: "14px",
            fontWeight: active ? 600 : 500,
            color: active ? "#1a1a1a" : "#666",
          }}
        >
          {label}
        </Typography>
        {unreadCount > 0 && (
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#EB0014",
            }}
          />
        )}
      </Stack>
    </ButtonBase>
  );
}

interface ListProps {
  items: NotificationsInterface[];
  notificationsViewed: NotificationsViewedInterface[];
  onItemClick: (notification: NotificationsInterface) => void;
}

function EmptyState({ message }: { message: string }) {
  return (
    <Box sx={{ padding: "40px 20px", textAlign: "center" }}>
      <Typography sx={{ fontSize: "14px", color: "#666" }}>{message}</Typography>
    </Box>
  );
}

function ActivityList({
  items,
  notificationsViewed,
  onItemClick,
  onDismiss,
}: ListProps & { onDismiss: (notification: NotificationsInterface) => void }) {
  if (items.length === 0) return <EmptyState message="No activity" />;
  return (
    <>
      {items.map((notification, index) => {
        const unread = isUnread(notification, notificationsViewed);
        return (
          <ActivityRow
            key={notification.id}
            notification={notification}
            unread={unread}
            isLast={index === items.length - 1}
            onClick={() => onItemClick(notification)}
            onDismiss={() => onDismiss(notification)}
          />
        );
      })}
    </>
  );
}

function AnnouncementList({ items, notificationsViewed, onItemClick }: ListProps) {
  if (items.length === 0) return <EmptyState message="No notifications" />;
  return (
    <>
      {items.map((notification, index) => {
        const unread = isUnread(notification, notificationsViewed);
        return (
          <AnnouncementRow
            key={notification.id}
            notification={notification}
            unread={unread}
            isLast={index === items.length - 1}
            onClick={() => onItemClick(notification)}
          />
        );
      })}
    </>
  );
}

function buildLinkProps(linkInfo: ParsedLink) {
  if (linkInfo.kind === "invalid") return {};
  if (linkInfo.kind === "external") {
    return {
      component: "a" as const,
      href: linkInfo.href,
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }
  return { component: "a" as const, href: linkInfo.href };
}

// Internal links must navigate via React Router; a plain <a> href triggers a
// full page reload that cancels the in-flight `notifications_viewed` write,
// leaving the notification unread after reload.
function useNotificationClickHandler() {
  const navigate = useNavigate();
  return (linkInfo: ParsedLink, onClick: () => void) =>
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const plainLeftClick =
        e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey;
      if (linkInfo.kind === "internal" && plainLeftClick) {
        e.preventDefault();
        onClick();
        navigate(linkInfo.href);
        return;
      }
      onClick();
    };
}

function ActivityRow({
  notification,
  unread,
  isLast,
  onClick,
  onDismiss,
}: {
  notification: NotificationsInterface;
  unread: boolean;
  isLast: boolean;
  onClick: () => void;
  onDismiss: () => void;
}) {
  const linkInfo = parseNotificationLink(notification.link);
  const linkProps = buildLinkProps(linkInfo);
  const handleRoutedClick = useNotificationClickHandler();
  return (
    <MenuItem
      {...linkProps}
      onClick={handleRoutedClick(linkInfo, onClick)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        color: "inherit",
        textDecoration: "none",
        whiteSpace: "normal",
        borderBottom: isLast ? "none" : "1px solid rgba(0, 0, 0, 0.08)",
        "&:hover": { backgroundColor: "#f7f7f7" },
      }}
    >
      {notification.image_url ? (
        <Box
          component="img"
          src={cloudflareCDN(notification.image_url, "width=160,fit=cover,format=auto")}
          alt=""
          sx={{
            width: "64px",
            height: "64px",
            borderRadius: "8px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Avatar
          variant="rounded"
          sx={{
            backgroundColor: "#f0f0f0",
            color: "#999",
            width: 64,
            height: 64,
            borderRadius: "8px",
            "& .MuiSvgIcon-root": { fontSize: "32px" },
          }}
        >
          {notifIcon(notification.type)}
        </Avatar>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: unread ? 600 : 500,
            color: "#1a1a1a",
            overflowWrap: "break-word",
          }}
        >
          {notification.title}
        </Typography>
        {notification.text && (
          <Typography
            sx={{
              fontSize: "12px",
              color: "#666",
              overflowWrap: "break-word",
            }}
          >
            {notification.text}
          </Typography>
        )}
        <Typography sx={{ fontSize: "11px", color: "#999", marginTop: "2px" }}>
          {formatRelativeShort(notification.updatedAt)}
        </Typography>
      </Box>
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="space-between"
        sx={{ alignSelf: "stretch" }}
      >
        <IconButton
          aria-label="dismiss notification"
          size="small"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDismiss();
          }}
          sx={{ padding: "2px" }}
        >
          <CloseIcon sx={{ fontSize: "16px", color: "#999" }} />
        </IconButton>
        {unread && (
          <Box
            sx={{
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#EB0014",
              marginBottom: "4px",
            }}
          />
        )}
      </Stack>
    </MenuItem>
  );
}

function AnnouncementRow({
  notification,
  unread,
  isLast,
  onClick,
}: {
  notification: NotificationsInterface;
  unread: boolean;
  isLast: boolean;
  onClick: () => void;
}) {
  const linkInfo = parseNotificationLink(notification.link);
  const linkProps = buildLinkProps(linkInfo);
  const handleRoutedClick = useNotificationClickHandler();
  const thumbnail = notification.image_url
    ? cloudflareCDN(notification.image_url, "width=160,fit=cover,format=auto")
    : null;
  return (
    <MenuItem
      {...linkProps}
      onClick={handleRoutedClick(linkInfo, onClick)}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: "12px",
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        color: "inherit",
        textDecoration: "none",
        whiteSpace: "normal",
        borderBottom: isLast ? "none" : "1px solid rgba(0, 0, 0, 0.08)",
        "&:hover": { backgroundColor: "#f7f7f7" },
      }}
    >
      {thumbnail ? (
        <Box
          component="img"
          src={thumbnail}
          alt=""
          sx={{
            width: "64px",
            height: "64px",
            borderRadius: "8px",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Box
          sx={{
            width: "64px",
            height: "64px",
            borderRadius: "8px",
            backgroundColor: "#f0f0f0",
            color: "#999",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <CampaignIcon sx={{ fontSize: "32px" }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: unread ? 600 : 500,
            color: "#1a1a1a",
            overflowWrap: "break-word",
          }}
        >
          {notification.title}
        </Typography>
        {notification.text && (
          <Typography
            sx={{
              fontSize: "12px",
              color: "#666",
              marginTop: "2px",
              overflowWrap: "break-word",
            }}
          >
            {notification.text}
          </Typography>
        )}
        <Typography sx={{ fontSize: "11px", color: "#999", marginTop: "4px" }}>
          {formatRelativeShort(notification.updatedAt)}
        </Typography>
      </Box>
      {unread && (
        <Box
          sx={{
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#EB0014",
            marginTop: "6px",
            flexShrink: 0,
          }}
        />
      )}
    </MenuItem>
  );
}
